import {
  type CompanyRole,
  computeThresholds,
  DEACTIVATION_BLOCK_LABELS,
  deactivationBlock,
  formatDateTime,
  formatMoney,
  formatNumber,
  type Member,
  ROLE_LABELS,
  UMA_CASH_LIMIT,
  UMA_IDENTIFICATION,
  UMA_NOTICE,
} from "@pcj/shared";
import { Check, Download, Trash2, UserCheck, UserPlus, UserX } from "lucide-react";
import { type FormEvent, useState } from "react";
import {
  Banner,
  Button,
  Card,
  CopyButton,
  Field,
  inputClass,
  Pill,
  SectionTitle,
  Spinner,
  Table,
  td,
  th,
} from "../../components/ui";
import { errorMessage, issueFor } from "../../lib/api";
import { useTeam, useTeamActions } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";

export function SettingsTab() {
  const { can } = useCompany();
  return (
    <div className="space-y-8">
      <CompanySettings />
      {can("manageAccess") && <TeamSettings />}
    </div>
  );
}

function CompanySettings() {
  const { company, can, actions, companyId } = useCompany();
  const editable = can("configure");
  const [form, setForm] = useState({
    legalName: company.legalName,
    legalForm: company.legalForm ?? "",
    rfc: company.rfc ?? "",
    taxAddress: company.taxAddress ?? "",
    complianceOfficer: company.complianceOfficer ?? "",
    umaValue: String(company.umaValue),
  });
  const [saved, setSaved] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });
  const uma = Number.parseFloat(form.umaValue);
  const thresholds = computeThresholds(Number.isFinite(uma) ? uma : 0);
  const error = actions.updateCompany.error;

  function submit(event: FormEvent) {
    event.preventDefault();
    actions.updateCompany.mutate(
      { ...form, umaValue: uma },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }

  return (
    <section className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <Card className="p-5">
        <SectionTitle title="Datos de la empresa" subtitle="Sujeto obligado ante el SAT" />
        <form onSubmit={submit} className="mt-4">
          <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2">
            <Field label="Razón social" required error={issueFor(error, "legalName")} className="sm:col-span-2">
              {(id) => (
                <input id={id} required className={inputClass} value={form.legalName} onChange={set("legalName")} />
              )}
            </Field>
            <Field label="Forma jurídica">
              {(id) => <input id={id} className={inputClass} value={form.legalForm} onChange={set("legalForm")} />}
            </Field>
            <Field label="RFC" error={issueFor(error, "rfc")}>
              {(id) => <input id={id} className={`${inputClass} uppercase`} value={form.rfc} onChange={set("rfc")} />}
            </Field>
            <Field label="Domicilio fiscal" className="sm:col-span-2">
              {(id) => <input id={id} className={inputClass} value={form.taxAddress} onChange={set("taxAddress")} />}
            </Field>
            <Field label="Responsable de Cumplimiento">
              {(id) => (
                <input
                  id={id}
                  className={inputClass}
                  value={form.complianceOfficer}
                  onChange={set("complianceOfficer")}
                />
              )}
            </Field>
            <Field
              label="Valor diario de la UMA (MXN)"
              error={issueFor(error, "umaValue")}
              hint="Actualízalo cada año cuando el INEGI publique el nuevo valor."
            >
              {(id) => (
                <input
                  id={id}
                  type="number"
                  step="0.01"
                  min="1"
                  required
                  className={inputClass}
                  value={form.umaValue}
                  onChange={set("umaValue")}
                />
              )}
            </Field>
          </fieldset>
          {error && !issueFor(error, "legalName") && !issueFor(error, "rfc") && (
            <div className="mt-3">
              <Banner tone="error">{errorMessage(error)}</Banner>
            </div>
          )}
          {editable && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button type="submit" loading={actions.updateCompany.isPending} icon={saved ? Check : undefined}>
                {saved ? "Guardado" : "Guardar cambios"}
              </Button>
              <a
                href={`/api/v1/companies/${companyId}/export`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                <Download className="h-4 w-4" aria-hidden /> Descargar respaldo (.json)
              </a>
            </div>
          )}
        </form>
      </Card>
      <Card className="h-fit p-5 text-sm text-gray-600">
        <p className="mb-3 font-medium text-gray-800">Umbrales calculados</p>
        <ul className="space-y-2">
          <li>
            Identificación ({formatNumber(UMA_IDENTIFICATION)} UMA, sin IVA)
            <strong className="block text-gray-900">{formatMoney(thresholds.identification)}</strong>
          </li>
          <li>
            Aviso ({formatNumber(UMA_NOTICE)} UMA, sin IVA, individual o acumulado)
            <strong className="block text-gray-900">{formatMoney(thresholds.notice)}</strong>
          </li>
          <li>
            Tope de efectivo ({formatNumber(UMA_CASH_LIMIT)} UMA, con IVA)
            <strong className="block text-gray-900">{formatMoney(thresholds.cashLimit)}</strong>
          </li>
        </ul>
      </Card>
    </section>
  );
}

/** The company admin invites and manages access; the chamber admin only turns access off or on. */
function TeamSettings() {
  const { companyId, can, me } = useCompany();
  const team = useTeam(companyId);
  const { invite, revoke, setActive } = useTeamActions(companyId);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CompanyRole>("operator");
  const lastInvite = invite.data;
  const canInvite = can("manageTeam");

  function submit(event: FormEvent) {
    event.preventDefault();
    invite.mutate({ email, role }, { onSuccess: () => setEmail("") });
  }

  function toggleAccess(member: Member) {
    const who = member.name ?? member.email;
    const active = member.deactivatedAt !== null;
    const question = active
      ? `¿Reactivar el acceso de ${who}? Podrá volver a iniciar sesión.`
      : `¿Desactivar el acceso de ${who}? Se cerrarán sus sesiones y ya no podrá entrar al portal. Sus registros y la bitácora se conservan, y puedes reactivar su acceso después.`;
    if (window.confirm(question)) setActive.mutate({ userId: member.userId, active });
  }

  return (
    <section className="space-y-3">
      <SectionTitle
        title="Equipo"
        subtitle={
          canInvite
            ? "Personas con acceso a esta empresa"
            : "Como Cámara puedes desactivar o reactivar el acceso de cualquier persona de esta joyería"
        }
      />
      {canInvite && (
        <Card className="p-4">
          <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Correo de la persona" className="flex-1" error={issueFor(invite.error, "email")}>
              {(id) => (
                <input
                  id={id}
                  type="email"
                  required
                  className={inputClass}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Rol">
              {(id) => (
                <select
                  id={id}
                  className={inputClass}
                  value={role}
                  onChange={(e) => setRole(e.target.value as CompanyRole)}
                >
                  <option value="operator">{ROLE_LABELS.operator}</option>
                  <option value="company_admin">{ROLE_LABELS.company_admin}</option>
                </select>
              )}
            </Field>
            <Button type="submit" icon={UserPlus} loading={invite.isPending}>
              Invitar
            </Button>
          </form>
          <p className="mt-2 text-xs text-gray-500">
            {ROLE_LABELS.operator}: solo captura clientes y operaciones; no ve la configuración ni puede eliminar
            registros.
          </p>
          {invite.error && !issueFor(invite.error, "email") && (
            <div className="mt-3">
              <Banner tone="error">{errorMessage(invite.error)}</Banner>
            </div>
          )}
          {lastInvite && (
            <div className="mt-3">
              <Banner
                tone="success"
                title={
                  lastInvite.emailSent
                    ? "Invitación enviada por correo"
                    : "Invitación creada (el correo no se pudo enviar)"
                }
              >
                <p className="mb-2">También puedes compartir este enlace directamente; vence en 7 días:</p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="break-all rounded bg-white px-2 py-1 text-xs">{lastInvite.link}</code>
                  <CopyButton text={lastInvite.link} label="Copiar enlace" />
                </div>
              </Banner>
            </div>
          )}
        </Card>
      )}

      {setActive.error && <Banner tone="error">{errorMessage(setActive.error)}</Banner>}
      {team.isPending ? (
        <Spinner label="Cargando equipo…" />
      ) : team.isError ? (
        <Banner tone="error">{errorMessage(team.error)}</Banner>
      ) : (
        <Table>
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>Persona</th>
              <th className={th}>Rol</th>
              <th className={th}>Estatus</th>
              <th className={th} aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {team.data.members.map((m) => {
              const who = m.name ?? m.email;
              const isMe = m.userId === me.user.id;
              const block = deactivationBlock(team.data.members, m, me.user.id);
              const pending = setActive.isPending && setActive.variables?.userId === m.userId;
              return (
                <tr key={m.userId}>
                  <td className={td}>
                    <p className="font-medium text-gray-900">
                      {who}
                      {isMe && <span className="ml-1.5 text-xs font-normal text-gray-500">(tú)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{m.email}</p>
                  </td>
                  <td className={`${td} text-gray-600`}>{ROLE_LABELS[m.role]}</td>
                  <td className={td}>
                    {m.deactivatedAt ? (
                      <>
                        <Pill>Desactivado</Pill>
                        <p className="mt-1 text-xs text-gray-500">desde {formatDateTime(m.deactivatedAt)}</p>
                      </>
                    ) : (
                      <Pill tone="green">Activo</Pill>
                    )}
                  </td>
                  <td className={`${td} text-right`}>
                    {m.deactivatedAt ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={UserCheck}
                        loading={pending}
                        onClick={() => toggleAccess(m)}
                        aria-label={`Reactivar acceso de ${who}`}
                      >
                        Reactivar
                      </Button>
                    ) : block === "last_company_admin" ? (
                      <span className="text-xs text-gray-500" title={DEACTIVATION_BLOCK_LABELS[block]}>
                        Único Responsable activo
                      </span>
                    ) : (
                      !block && (
                        <Button
                          variant="danger"
                          size="sm"
                          icon={UserX}
                          loading={pending}
                          onClick={() => toggleAccess(m)}
                          aria-label={`Desactivar acceso de ${who}`}
                        >
                          Desactivar acceso
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
            {team.data.invitations.map((inv) => (
              <tr key={inv.id}>
                <td className={td}>
                  <p className="font-medium text-gray-900">{inv.email}</p>
                  <p className="text-xs text-gray-500">Invitación vence {formatDateTime(inv.expiresAt)}</p>
                </td>
                <td className={`${td} text-gray-600`}>{ROLE_LABELS[inv.role]}</td>
                <td className={td}>
                  <Pill tone="amber">Pendiente</Pill>
                </td>
                <td className={`${td} text-right`}>
                  {canInvite && (
                    <Button
                      variant="danger"
                      size="sm"
                      icon={Trash2}
                      onClick={() => revoke.mutate(inv.id)}
                      aria-label={`Cancelar invitación de ${inv.email}`}
                    >
                      Cancelar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
